import StarTwoTone from '@ant-design/icons/StarTwoTone';
import { Typography } from 'antd';
import cx from 'classnames';
import { ComponentPropsWithRef, cloneElement, ReactElement, ReactNode } from 'react';

import { Size } from '../../@types/props';
import colors from '../../theme/colors';
import Avatar from '../Avatar';
import Space from '../Space';
import { TagFeedback } from '../TagFeedback';

import styles from './Contact.module.less';

export interface ContactDescriptionProps {
  qualifications?: ReactNode;
  address?: ReactNode;
  bornOn?: ReactNode;
  ins?: ReactNode;
  nir?: ReactNode;
  ipp?: ReactNode;
  insQualification?: ReactNode;
}

const { Text } = Typography;

interface ContactProps extends ComponentPropsWithRef<'div'> {
  title: string | undefined;
  subtitle?: ReactElement | string;
  description?: ContactDescriptionProps;
  initials: string | undefined;
  hasStar?: boolean;
  onStar?: () => void;
  isStarred?: boolean;
  size?: Size;
  showAvatar?: boolean;
  avatarColor?: string;
  customAvatarSize?: number;
  tag?: string;
  icon?: ReactElement;
}

const Subtitle = ({ inline, subtitle }: { inline?: boolean; subtitle: ReactElement | string }) =>
  typeof subtitle === 'string' ? (
    <Text className={cx(styles.subtitle, inline && styles.inline)}>{subtitle}</Text>
  ) : (
    cloneElement(
      subtitle as ReactElement,
      {
        className: cx((subtitle as any).props.className, styles.subtitle),
      } as any, // Due to update of cloneElement type this fail to recognize props.className as a valid props
    )
  );

const Description = ({ description }: { description: ContactDescriptionProps }) => (
  <Space direction="vertical" size={2} data-testid="contact-description">
    {Object.entries(description).map(([key, value]) => (
      <Text className={styles.description} key={key} data-testid={`contact-description-${key}`}>
        {value}
      </Text>
    ))}
  </Space>
);

const Star = ({ isStarred, onStar }: { isStarred?: boolean; onStar?: () => void }) => (
  <StarTwoTone
    className={styles.star}
    twoToneColor={isStarred ? colors.gold[5] : colors.grey[5]}
    onClick={onStar}
  />
);

export const Contact = ({
  title,
  subtitle,
  description,
  initials,
  hasStar = false,
  onStar,
  isStarred,
  showAvatar = true,
  avatarColor,
  size = 'small',
  className,
  customAvatarSize,
  tag,
  icon,
  ...otherProps
}: ContactProps) => {
  if (size === 'large') {
    return (
      <Space align="start" size="large" {...otherProps} className={className}>
        {showAvatar && (
          <Avatar size={56} className={styles.avatar} background={avatarColor}>
            {initials}
          </Avatar>
        )}
        <Space size={4} direction="vertical" className={styles.textContainer}>
          <Space>
            <Text className={styles.title}>{title}</Text>
            {hasStar && <Star isStarred={isStarred} onStar={onStar} />}
          </Space>
          {subtitle && <Subtitle subtitle={subtitle} />}
          {description && Object.keys(description).length > 0 && (
            <Description description={description} />
          )}
        </Space>
      </Space>
    );
  }
  if (size === 'medium') {
    return (
      <Space
        align="center"
        size="middle"
        {...otherProps}
        className={cx('contactMediumSize', className)}
      >
        {showAvatar && !icon && (
          <Avatar color={colors.white[0]} background={colors.grey[6]} size="large">
            {initials}
          </Avatar>
        )}
        {showAvatar && icon && icon}
        <Space size={0} direction="vertical">
          <Space>
            <Text className={styles.title}>{title}</Text>
            {tag && (
              <TagFeedback status="information" className={styles.tag}>
                {tag}
              </TagFeedback>
            )}
            {subtitle && <Subtitle subtitle={subtitle} />}
            {hasStar && <Star isStarred={isStarred} onStar={onStar} />}
          </Space>
          {description && Object.keys(description).length > 0 && (
            <Description description={description} />
          )}
        </Space>
      </Space>
    );
  }
  return (
    <Space {...otherProps} className={className}>
      <div className={styles.container}>
        {showAvatar && (
          <Avatar size={customAvatarSize ?? undefined} background={avatarColor}>
            {initials}
          </Avatar>
        )}
      </div>
      <Text>{title}</Text>
      {subtitle && <Subtitle subtitle={subtitle} inline />}
    </Space>
  );
};
